import React, { useEffect, useState } from "react";
import { slide as SideBar } from "react-burger-menu";
import DonationList from "./DonationList";
import OrderTotal from "./OrderTotal";
import FrequencyToggle from "./FrequencyToggle";
import "./FundSideBar.scss";
import "./HelpTip.scss";
import "../spinner.scss";
import { useAppContext } from "../hooks/useAppContext";
import { getCampaignPath } from "../services/funds.service";
import { hasEmptyAmount, findFirstEmptyAmount } from "../services/cart.service";

const FundSideBar = () => {
  const [editStateItems, setEditStateItems] = React.useState(null);
  const [readyCheckout, setReadyCheckout] = React.useState(true);
  const [showHelpTip, setShowHelpTip] = React.useState(false);
  const {
    frequency,
    items,
    stateChangeHandler,
    isMenuOpen,
    total,
    toggleMenu,
    loadOrderItemList,
    isLoadingNewItem,
    innerWidth,
  } = useAppContext();

  let styles = {
    bmBurgerButton: {
      position: "fixed",
      width: "36px",
      height: "30px",
      left: "36px",
      top: "36px",
    },
    bmBurgerBars: {
      background: "#373a47",
    },
    bmBurgerBarsHover: {
      background: "#a90000",
    },
    bmCrossButton: {
      height: "24px",
      width: "24px",
    },
    bmCross: {
      background: "#bdc3c7",
    },
    bmMenuWrap: {
      position: "fixed",
      height: "100%",
      width: innerWidth >= 576 ? "23rem" : "calc(100vw - 2rem)",
      top: 0,
    },
    bmMenu: {
      background: "#fff",
      padding: "0",
      fontSize: "1.15em",
      overflowY: "scroll",
      scrollBehavior: "smooth",
    },
    bmMorphShape: {
      fill: "#373a47",
    },
    bmItemList: {
      color: "#012169",
    },
    bmItem: {
      display: "block",
    },
    bmOverlay: {
      background: "rgba(62, 62, 62, .8)",
      top: 0,
      left: 0,
    },
  };

  // @todo add logic to determine if there are items in cart.
  let hasItems = true;
  const isCategoryRegexp = new RegExp(/^\/(give|give\/to\/.+)$/);
  const isCategory = location.pathname.match(isCategoryRegexp);

  const countItems = items.length;
  const countEmptyItems = items.filter(
    (v) => parseInt(v.attributes.total_price.number) === 0
  ).length;
  const helpTip =
    countEmptyItems == 1 && countItems == 1 ? (
      <>To continue, select a gift amount.</>
    ) : countEmptyItems == 1 && countItems > 1 ? (
      <>To continue, select an amount or remove gift.</>
    ) : countEmptyItems > 1 && countItems == countEmptyItems ? (
      <>To continue, select amounts and/or remove gifts.</>
    ) : countEmptyItems > 1 && countItems != countEmptyItems ? (
      <>To continue, select amounts or remove gifts.</>
    ) : (
      <></>
    );

  function scrollToEmptyCartItem() {
    document.querySelectorAll(".bm-menu").forEach((sidebar) => {
      const selector = ".order-item__wrapper--edit-donation";
      const firstEmptyItem = sidebar.querySelectorAll(selector)[0];
      const top = sidebar.querySelector(".sidebar__top");
      const middle = sidebar.querySelector(".sidebar__middle");
      if (firstEmptyItem && top && middle) {
        // Account for height of the sticky sidebar__top part overlaying the
        // middle area which scrolls underneath, to avoid partially covering
        // the first empty item.
        const itemOffset = firstEmptyItem.offsetTop;
        // Account for the middle section top padding to avoid partially
        // covering the first empty item underneath the "fade out".
        const paddingTop = Math.round(
          parseFloat(
            window
              .getComputedStyle(middle, null)
              .getPropertyValue("padding-top")
          )
        );
        const scrollOffset = top.offsetHeight + paddingTop;
        if (itemOffset > scrollOffset) {
          sidebar.scrollTop = itemOffset - scrollOffset;
        }
      }
    });
  }

  function handleMakeYourGiftClick(event) {
    if (hasEmptyAmount(items)) {
      event.target.preventDefault;
      setShowHelpTip(true);
      setReadyCheckout(false);
      scrollToEmptyCartItem();
      return;
    }
    window.location.href = "/give/checkout";
  }

  // Scroll user to first empty item when sidebar opens.
  useEffect(() => {
    if (
      isMenuOpen &&
      items.length &&
      !isLoadingNewItem &&
      findFirstEmptyAmount(items)
    ) {
      scrollToEmptyCartItem();
    }
  }, [items, isMenuOpen, isLoadingNewItem]);

  // Only show help tip after user clicks "continue" and something is
  // preventing continuing to checkout.
  useEffect(() => {
    setShowHelpTip(showHelpTip && !readyCheckout);
  }, [showHelpTip, readyCheckout]);

  return (
    <SideBar
      styles={styles}
      right
      customBurgerIcon={false}
      customCrossIcon={false}
      isOpen={isMenuOpen}
      onStateChange={(state) => stateChangeHandler(state)}
    >
      {hasItems ? (
        <>
          <div className="sidebar">
            {isLoadingNewItem ? (
              <div className="loader-overlay">
                <div className="spinner spinner--centered"></div>
              </div>
            ) : null}
            <div className="sidebar__top">
              <div
                className={`sidebar__back blur${
                  isLoadingNewItem ? " blurred" : ""
                }`}
              >
                <button
                  onClick={(e) => {
                    if (!isCategory) {
                      window.location.href = getCampaignPath();
                    }
                    toggleMenu(e);
                  }}
                  className="sidebar__back-button"
                >
                  Add Another Gift
                </button>
              </div>
              <h3
                className={`sidebar__title blur${
                  isLoadingNewItem ? " blurred" : ""
                }`}
              >
                Your Gifts
              </h3>
              {!isLoadingNewItem ? <FrequencyToggle /> : null}
            </div>
            {!isLoadingNewItem ? (
              <>
                <div className="sidebar__middle">
                  <DonationList
                    setReadyCheckout={setReadyCheckout}
                    editStateItems={editStateItems}
                    setEditStateItems={setEditStateItems}
                    className={`order-item-list--sidebar${
                      items.length && items.length > 1
                        ? " has-multiple-items"
                        : ""
                    }`}
                  />
                </div>
                <div className="sidebar__bottom">
                  {items.length > 1 ? (
                    <OrderTotal frequency={frequency} total={total} />
                  ) : null}
                  {showHelpTip ? (
                    <div className="help-tip help-tip--bottom-left help-tip--sidebar-continue-button">
                      {helpTip}
                    </div>
                  ) : null}
                  <button
                    data-hook="make-your-gift-btn"
                    type="button"
                    className={`coh-style-link-button-with-icon--secondary ${
                      loadOrderItemList ? "loading" : ""
                    }`}
                    onClick={handleMakeYourGiftClick}
                    disabled={loadOrderItemList}
                  >
                    Make Your Gift
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <h4>Your cart is currently empty.</h4>
          <a href={getCampaignPath()}>Browse our areas of need</a>
        </>
      )}
    </SideBar>
  );
};

export default FundSideBar;
